import type {
  CollectionReference,
  DocumentData,
  QueryCompositeFilterConstraint,
  QueryConstraint,
  QueryFilterConstraint,
  Timestamp,
  Unsubscribe,
} from 'firebase/firestore';
import {
  Timestamp as FirestoreTimestamp,
  addDoc,
  and,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  or,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { db } from './config';

// Business Types interfaces
export interface BusinessTypeHistoryEntry {
  id: string;
  name: string;
  output: number;
  request: string;
  ts: Timestamp;
  type: string;
}

export interface BusinessTypeMetadata {
  industry_painpoints: string[];
  website_objectives: string[];
}

// Apollo Sequence interfaces
export interface ApolloSequence {
  name: string;
  sequence_id: string;
}

// Sequence action types for apollo_contacts subcollection
export type SequenceActionStatus = 'pending' | 'completed' | 'failed' | 'skipped';

export interface SequenceAction {
  sequence_id: string;
  status: SequenceActionStatus;
  skipped_reason?: string;
  error_message?: string;
  completed_at?: Timestamp;
}

export interface ApolloContactDoc {
  sequence_action?: SequenceAction;
}

// Apollo events interfaces (leads/{leadId}/apollo_events/{contactId})
export interface ApolloEmailEvent {
  due_at?: string;
  status?: string;
  subject?: string;
  contact_id?: string;
  campaign_name?: string;
  from_email?: string;
  from_name?: string;
  to_email?: string;
  to_name?: string;
  completed_at?: string;
  created_at?: string;
  bounce?: string | null;
  failed_at?: string | null;
  failure_reason?: string | null;
  not_sent_reason?: string | null;
  replied?: string | null;
  reply_class?: string | null;
  spam_blocked?: string | null;
  type?: string;
  id?: string;
  emailer_campaign_id?: string;
}

export interface ApolloEventDoc {
  id: string;
  emails: Record<string, ApolloEmailEvent>;
  last_updated?: Timestamp;
}

// Basic business type from list (scopes/types)
export interface BusinessTypeListItem {
  name: string;
  output: number;
  slug: string;
  ts: Timestamp;
}

// Full business type with details (scopes/types/business_types/{slug})
export interface BusinessType extends BusinessTypeListItem {
  id: string;
  history?: BusinessTypeHistoryEntry[];
  requests?: BusinessTypeRequest[];
  metadata?: BusinessTypeMetadata;
  default_sequence_id?: string;
}

export interface NewBusinessType {
  name: string;
  slug: string;
}

export const firestoreService = {
  // Get a single document
  getDocument: async <T = DocumentData>(
    collectionName: string,
    documentId: string
  ): Promise<T | null> => {
    const docRef = doc(db, collectionName, documentId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as T;
    }
    return null;
  },

  // Get multiple documents with optional filters
  getDocuments: async <T = DocumentData>(
    collectionName: string,
    constraints: QueryConstraint[] = []
  ): Promise<T[]> => {
    const collectionRef = collection(db, collectionName) as CollectionReference<DocumentData>;
    const q = query(collectionRef, ...constraints);
    const querySnapshot = await getDocs(q);

    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as T[];
  },

  // Add a new document
  addDocument: async <T extends DocumentData = DocumentData>(
    collectionName: string,
    data: T
  ): Promise<string> => {
    const collectionRef = collection(db, collectionName);
    const docRef = await addDoc(collectionRef, data);
    return docRef.id;
  },

  // Update a document
  updateDocument: async (
    collectionName: string,
    documentId: string,
    data: Partial<DocumentData>
  ): Promise<void> => {
    const docRef = doc(db, collectionName, documentId);
    return updateDoc(docRef, data);
  },

  // Delete a document
  deleteDocument: async (collectionName: string, documentId: string): Promise<void> => {
    const docRef = doc(db, collectionName, documentId);
    return deleteDoc(docRef);
  },

  // Query helpers
  where,
  orderBy,
  limit,
};

// Business Types functions
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getBusinessTypesList = async (): Promise<any[]> => {
  try {
    const docRef = doc(db, 'scopes', 'types');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.business_types || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting business types list:', error);
    throw error;
  }
};

export const subscribeToBusinessTypesList = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callback: (businessTypes: any[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const docRef = doc(db, 'scopes', 'types');

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          const businessTypes = data.business_types || [];
          callback(businessTypes);
        } else {
          callback([]);
        }
      },
      (error) => {
        console.error('Error subscribing to business types list:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up business types list subscription:', error);
    onError?.(error as Error);
    return () => {}; // Return empty unsubscribe function
  }
};

// Register a new Apollo saved list (SC-2193). Creates a document at
// `lists/{apolloListId}` so the ingestion trigger (SC-2143) can fan out
// its companies into the `leads` collection. Rejects duplicates so a
// re-submission never overwrites an already-ingested list.
export class ListAlreadyExistsError extends Error {
  constructor(listId: string) {
    super(`List "${listId}" is already registered.`);
    this.name = 'ListAlreadyExistsError';
  }
}

export const createList = async (apolloListId: string): Promise<void> => {
  const trimmed = apolloListId.trim();
  if (!trimmed) throw new Error('Apollo List ID is required.');

  const docRef = doc(db, 'lists', trimmed);
  const existing = await getDoc(docRef);
  if (existing.exists()) {
    throw new ListAlreadyExistsError(trimmed);
  }
  await setDoc(docRef, {
    id: trimmed,
    created: serverTimestamp(),
  });
};

// Subscribe to the top-level `lists` collection (Apollo saved lists — SC-2143)
export const subscribeToLists = (
  callback: (lists: LeadList[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const collectionRef = collection(db, 'lists');
    return onSnapshot(
      collectionRef,
      (snapshot) => {
        const lists: LeadList[] = snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          name: (docSnap.data().name as string) || docSnap.id,
        }));
        lists.sort((a, b) => a.name.localeCompare(b.name));
        callback(lists);
      },
      (error) => {
        console.error('Error subscribing to lists:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up lists subscription:', error);
    onError?.(error as Error);
    return () => {};
  }
};

export const getBusinessType = async (slug: string): Promise<BusinessType | null> => {
  try {
    const docRef = doc(db, 'scopes', 'types', 'business_types', slug);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        name: data.name,
        slug: data.slug || slug,
        output: data.output || 0,
        history: data.history || [],
        requests: data.requests || [],
        metadata: data.metadata || { industry_painpoints: [], website_objectives: [] },
        default_sequence_id: data.default_sequence_id,
      } as BusinessType;
    }
    return null;
  } catch (error) {
    console.error('Error getting business type:', error);
    throw error;
  }
};

// Subscribe to real-time updates for a specific business type
export const subscribeToBusinessType = (
  slug: string,
  onUpdate: (businessType: BusinessType | null) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const docRef = doc(db, 'scopes', 'types', 'business_types', slug);

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();

          const businessType: BusinessType = {
            id: docSnap.id,
            name: data.name,
            slug: data.slug || slug,
            output: data.output || 0,
            ts: data.ts,
            history: data.history || [],
            requests: data.requests || [],
            metadata: data.metadata || { industry_painpoints: [], website_objectives: [] },
            default_sequence_id: data.default_sequence_id,
          };

          onUpdate(businessType);
        } else {
          onUpdate(null);
        }
      },
      (error) => {
        onError?.(error);
      }
    );
  } catch (error) {
    onError?.(error as Error);
    return () => {}; // Return empty unsubscribe function
  }
};

// Function to enrich a basic business type list item with full details
export const getFullBusinessTypeDetails = async (
  basicBusinessType: BusinessTypeListItem
): Promise<BusinessType | null> => {
  try {
    const fullDetails = await getBusinessType(basicBusinessType.slug);
    if (fullDetails) {
      return {
        id: basicBusinessType.slug,
        ...basicBusinessType,
        ts: basicBusinessType.ts,
        history: fullDetails.history || [],
        requests: fullDetails.requests || [],
        metadata: fullDetails.metadata || { industry_painpoints: [], website_objectives: [] },
      };
    }

    // If no full details found, return the basic item with empty history/metadata/requests
    return {
      id: basicBusinessType.slug,
      ...basicBusinessType,
      ts: basicBusinessType.ts,
      history: [],
      requests: [],
      metadata: { industry_painpoints: [], website_objectives: [] },
    };
  } catch (error) {
    console.error('Error getting full business type details:', error);
    // Return basic item with empty details on error
    return {
      id: basicBusinessType.slug,
      ...basicBusinessType,
      history: [],
      requests: [],
      ts: basicBusinessType.ts,
      metadata: { industry_painpoints: [], website_objectives: [] },
    };
  }
};

export const businessTypeExists = async (slug: string): Promise<boolean> => {
  try {
    const businessType = await getBusinessType(slug);
    return businessType !== null;
  } catch (error) {
    console.error('Error checking if business type exists:', error);
    return false;
  }
};

export const createBusinessType = async (businessType: NewBusinessType): Promise<void> => {
  const docRef = doc(db, 'scopes', 'types', 'business_types', businessType.slug);
  const data = {
    name: businessType.name,
    slug: businessType.slug,
  };

  await setDoc(docRef, data);
};

export const updateBusinessType = async (
  slug: string,
  updates: Partial<NewBusinessType>
): Promise<void> => {
  try {
    const docRef = doc(db, 'scopes', 'types', 'business_types', slug);
    await setDoc(docRef, updates, { merge: true });
  } catch (error) {
    console.error('Error updating business type:', error);
    throw error;
  }
};

export const updateBusinessTypeMetadata = async (
  slug: string,
  metadata: BusinessTypeMetadata
): Promise<void> => {
  try {
    const docRef = doc(db, 'scopes', 'types', 'business_types', slug);
    await updateDoc(docRef, {
      metadata: metadata,
    });
  } catch (error) {
    console.error('Error updating business type metadata:', error);
    throw error;
  }
};

export const deleteBusinessType = async (slug: string): Promise<void> => {
  try {
    // Delete the subcollection document
    const subDocRef = doc(db, 'scopes', 'types', 'business_types', slug);
    await deleteDoc(subDocRef);

    // Remove from the parent document's business_types array
    const parentDocRef = doc(db, 'scopes', 'types');
    const parentSnap = await getDoc(parentDocRef);
    if (parentSnap.exists()) {
      const data = parentSnap.data();
      const businessTypes = data.business_types || [];
      const filtered = businessTypes.filter((bt: BusinessTypeListItem) => bt.slug !== slug);
      await updateDoc(parentDocRef, { business_types: filtered });
    }
  } catch (error) {
    console.error('Error deleting business type:', error);
    throw error;
  }
};

export const createSlug = (name: string): string => {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

export const isValidBusinessTypeName = (name: string): boolean => {
  const trimmedName = name.trim();
  return /^[A-Za-z\s]+$/.test(trimmedName) && trimmedName.length > 0;
};

// Request interface for business type requests
export interface BusinessTypeRequest {
  name: string;
  id: number;
  request: number;
  type: 'gemini' | 'apollo';
}

// City interfaces
export interface City {
  id: string;
  name: string;
  region?: string;
  country?: string;
  population?: number;
}

// Lead/Site interfaces
export interface LeadContact {
  apollo?: {
    total_entries: number;
    people?: Array<{
      id: string;
      first_name: string;
      last_name_obfuscated: string;
      title?: string;
      has_email: boolean;
      has_direct_phone?: string;
      has_city?: boolean;
      has_state?: boolean;
      has_country?: boolean;
      last_refreshed_at?: string;
      enrich?: boolean;
      organization?: {
        name?: string;
        has_employee_count?: boolean;
        has_city?: boolean;
        has_state?: boolean;
        has_country?: boolean;
        has_phone?: boolean;
        has_industry?: boolean;
        has_revenue?: boolean;
        has_zip_code?: boolean;
      };
    }>;
    people_enriched?: Array<{
      id: string;
      name: string;
      first_name?: string;
      last_name?: string;
      headline?: string;
      photo_url?: string;
      email?: string;
      linkedin_url?: string;
      title?: string;
      organization?: {
        name?: string;
        has_employee_count?: boolean;
        has_city?: boolean;
        has_state?: boolean;
        has_country?: boolean;
        has_phone?: boolean;
        has_industry?: boolean;
        has_revenue?: boolean;
        has_zip_code?: boolean;
      };
      /** Allow additional fields from Apollo enrichment response */
      [key: string]: unknown;
    }>;
  };
  whatsappNumber?: string;
  phoneNumber?: string;
  email?: string;
  contactPageUrl?: string;
  socials?: {
    facebook?: string;
    instagram?: string;
    linkedin?: string;
    tiktok?: string;
    twitter?: string;
    youtube?: string;
    whatsapp?: string;
  };
}

export interface LeadScope {
  business_type?: string;
  cities?: {
    id: string;
  };
  source?: string; // e.g. "list:{listId}" for Apollo saved-list ingestion (SC-2143)
}

export interface LeadList {
  id: string;
  name: string;
}

export interface Lead {
  id: string;
  name: string;
  slug?: string;
  url?: string;
  urlCount?: number;
  created?: Timestamp;
  status?: 'lost' | 'converted';
  contacted?: boolean; // Root-level flag set by backend when a sequence completes
  notOK?: boolean;
  markedOK?: boolean;
  onboard_uid?: string; // Trigger for onboarding flow
  site_id?: string; // Demo site ID (set when onboarding completes)
  site_url?: string; // Bare host of the lead's site, no protocol (e.g. "taw.global")
  transfer_code?: string; // Per-lead transfer code used for personalised demo links (e.g. "trf_…")
  onboarded_by?: string; // UID of the user who onboarded this lead
  'site-explorer'?: {
    isFramer?: boolean;
    isShopify?: boolean;
    isWoocommerce?: boolean;
    isWordpress?: boolean;
    isMagento?: boolean;
  };
  contacts?: LeadContact;
  scope?: LeadScope;
  metadata?: {
    updated_at?: Timestamp;
    website_objectives?: string[];
    industry_painpoints?: string[];
  };
  person_id_to_contact_id?: Record<string, string | null>;
  demo_gif?: {
    regenerate?: boolean;
  };
}

// Filter types
export type TechnologyType =
  | 'framer'
  | 'shopify'
  | 'magento'
  | 'woocommerce'
  | 'wordpress'
  | 'unknown';
export type ContactInfoType = 'all-contacts' | 'has-email' | 'has-whatsapp' | 'no-contacts';
export type LeadStatusType =
  | 'not-onboarded'
  | 'not-contacted'
  | 'not-enriched'
  | 'onboarded'
  | 'onboarded-by-me'
  | 'contacted'
  | 'enriched'
  | 'converted'
  | 'lost'
  | 'marked-ok'
  | 'marked-not-ok'
  | 'unmarked';

// Add requests to a business type's requests array
export const addBusinessTypeRequests = async (
  slug: string,
  requests: BusinessTypeRequest[]
): Promise<void> => {
  try {
    const docRef = doc(db, 'scopes', 'types', 'business_types', slug);

    // Add each request to the requests array
    await updateDoc(docRef, {
      requests: arrayUnion(...requests),
    });
  } catch (error) {
    console.error('Error adding business type requests:', error);
    throw error;
  }
};

// Cities functions
export const getCitiesList = async (): Promise<City[]> => {
  try {
    const docRef = doc(db, 'scopes', 'cities');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const docData = docSnap.data();
      const cities = docData.data || [];

      // Sort alphabetically by name
      return cities.sort((a: City, b: City) => a.name.localeCompare(b.name));
    }
    return [];
  } catch (error) {
    console.error('Error getting cities list:', error);
    throw error;
  }
};

export const subscribeToCitiesList = (
  callback: (cities: City[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const docRef = doc(db, 'scopes', 'cities');

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const docData = docSnap.data();
          const cities = docData.data || [];

          // Sort alphabetically by name
          const sortedCities = cities.sort((a: City, b: City) => a.name.localeCompare(b.name));
          callback(sortedCities);
        } else {
          callback([]);
        }
      },
      (error) => {
        console.error('Error subscribing to cities list:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up cities list subscription:', error);
    onError?.(error as Error);
    return () => {};
  }
};

// Leads query filters interface
export interface LeadsQueryFilters {
  businessType?: string[]; // slugs
  listId?: string[]; // Apollo list IDs; matched against scope.source == `list:{id}` (SC-2143)
  cityId?: string[];
  technology?: TechnologyType[];
  contactInfo?: ContactInfoType[];
  leadStatus?: LeadStatusType[];
  currentUserUid?: string; // The connected account's onboard_uid; required when 'onboarded-by-me' filter is active
}

// Build query constraints for leads based on filters
interface LeadsQueryConstraintResult {
  compositeFilter?: QueryCompositeFilterConstraint;
  constraints: QueryConstraint[];
}

const buildLeadsQueryConstraints = (filters: LeadsQueryFilters): LeadsQueryConstraintResult => {
  const constraints: QueryConstraint[] = [];
  const composites: QueryCompositeFilterConstraint[] = [];

  // Types filter — unified across business types (scope.business_type) and Apollo lists
  // (scope.source == "list:{id}"). Selections across the two sources are ORed together (SC-2171).
  const hasBusinessTypes = !!filters.businessType && filters.businessType.length > 0;
  const hasLists = !!filters.listId && filters.listId.length > 0;
  const listSources = hasLists ? filters.listId!.map((id) => `list:${id}`) : [];

  const buildBusinessTypeConstraint = () =>
    filters.businessType!.length === 1
      ? where('scope.business_type', '==', filters.businessType![0])
      : where('scope.business_type', 'in', filters.businessType!);

  const buildListConstraint = () =>
    listSources.length === 1
      ? where('scope.source', '==', listSources[0])
      : where('scope.source', 'in', listSources);

  if (hasBusinessTypes && hasLists) {
    // OR across the two sources — needs a composite filter
    composites.push(or(buildBusinessTypeConstraint(), buildListConstraint()));
  } else if (hasBusinessTypes) {
    constraints.push(buildBusinessTypeConstraint());
  } else if (hasLists) {
    constraints.push(buildListConstraint());
  }

  // City filter
  if (filters.cityId && filters.cityId.length > 0) {
    if (filters.cityId.length === 1) {
      constraints.push(where('scope.cities.id', '==', filters.cityId[0]));
    } else {
      constraints.push(where('scope.cities.id', 'in', filters.cityId));
    }
  }

  // Technology filter — server-side for all known-tech selections (no 'unknown')
  if (filters.technology && filters.technology.length > 0) {
    const hasUnknown = filters.technology.includes('unknown');
    const knownTechs = filters.technology.filter((t) => t !== 'unknown');
    if (!hasUnknown && knownTechs.length === 1) {
      // Single known tech — simple equality constraint
      const tech = knownTechs[0]!;
      constraints.push(where(`site-explorer.is${tech.charAt(0).toUpperCase() + tech.slice(1)}`, '==', true));
    } else if (!hasUnknown && knownTechs.length > 1) {
      // Multiple known techs — OR composite constraint (Firestore supports up to 30 disjunctions).
      // QueryCompositeFilterConstraint cannot be mixed into QueryConstraint[] — must be passed
      // as the first argument to query() with remaining constraints as subsequent arguments.
      composites.push(
        or(
          ...knownTechs.map((tech) =>
            where(`site-explorer.is${tech.charAt(0).toUpperCase() + tech.slice(1)}`, '==', true)
          )
        )
      );
    }
    // If 'unknown' is included, skip server-side constraint — handled entirely client-side
  }

  // Contact info — only server-side when exactly one 'has-email' is selected
  if (filters.contactInfo && filters.contactInfo.length === 1 && filters.contactInfo[0] === 'has-email') {
    constraints.push(where('contacts.apollo.total_entries', '>', 0));
  }
  // Other contactInfo values are handled client-side

  // "Onboarded by Me" — server-side equality filter on onboarded-by field
  if (filters.leadStatus?.includes('onboarded-by-me') && filters.currentUserUid) {
    constraints.push(where('onboarded_by', '==', filters.currentUserUid));
  }

  // Collapse zero, one, or many composites into a single top-level compositeFilter.
  // Multiple composites are combined with `and()` so the query builder still receives
  // exactly one composite (plain constraints are ANDed alongside it downstream).
  const compositeFilter =
    composites.length === 0
      ? undefined
      : composites.length === 1
        ? composites[0]
        : and(...composites);

  return { compositeFilter, constraints };
};

// Client-side filter for leads that can't be handled by Firestore queries
const applyClientSideFilters = (leads: Lead[], filters: LeadsQueryFilters): Lead[] => {
  return leads.filter((lead) => {
    // Hide terminal states (lost / converted) from operational filter results
    // unless the user explicitly opts in via the Lead Status filter. Lost and
    // Converted leads are archived — they should not clutter the active queue
    // when an operator is filtering by Not Onboarded, Not Enriched, Onboarded,
    // etc. (SC-1450)
    if (lead.status === 'lost' || lead.status === 'converted') {
      const optedIn = filters.leadStatus?.includes(lead.status) ?? false;
      if (!optedIn) return false;
    }

    // Technology filter (client-side only when 'unknown' is in the selection)
    if (filters.technology && filters.technology.length > 0) {
      // Skip client-side check when all selected techs are known — handled server-side via or()
      const handledServerSide = !filters.technology.includes('unknown');
      if (!handledServerSide) {
        const explorer = lead['site-explorer'];
        const matchesAny = filters.technology.some((tech) => {
          if (tech === 'unknown') {
            const hasKnownTech =
              explorer?.isFramer ||
              explorer?.isShopify ||
              explorer?.isWoocommerce ||
              explorer?.isWordpress ||
              explorer?.isMagento;
            return !hasKnownTech;
          }
          const key = `is${tech.charAt(0).toUpperCase() + tech.slice(1)}` as keyof typeof explorer;
          return explorer?.[key] === true;
        });
        if (!matchesAny) return false;
      }
    }

    // Contact info filters (OR within)
    if (filters.contactInfo && filters.contactInfo.length > 0) {
      // Skip client-side check if already handled server-side (single 'has-email')
      const handledServerSide = filters.contactInfo.length === 1 && filters.contactInfo[0] === 'has-email';
      if (!handledServerSide) {
        const hasEmail = (lead.contacts?.apollo?.total_entries || 0) > 0;
        const hasWhatsApp = !!lead.contacts?.whatsappNumber && lead.contacts.whatsappNumber !== '';
        const hasSocials =
          lead.contacts?.socials && Object.values(lead.contacts.socials).some((value) => !!value);

        const matchesAny = filters.contactInfo.some((ci) => {
          switch (ci) {
            case 'all-contacts': return hasEmail || hasWhatsApp || (hasSocials ?? false);
            case 'has-email': return hasEmail;
            case 'has-whatsapp': return hasWhatsApp;
            case 'no-contacts': return !hasEmail && !hasWhatsApp;
            default: return true;
          }
        });
        if (!matchesAny) return false;
      }
    }

    // Lead status filter (AND across selections, all client-side)
    if (filters.leadStatus && filters.leadStatus.length > 0) {
      const matchesAll = filters.leadStatus.every((ls) => {
        switch (ls) {
          case 'not-onboarded': return !lead.site_id;
          case 'not-contacted': return lead.contacted !== true;
          case 'onboarded': return !!lead.site_id;
          case 'onboarded-by-me': return !!lead.onboarded_by && lead.onboarded_by === filters.currentUserUid;
          case 'contacted': return lead.contacted === true;
          case 'converted': return lead.status === 'converted';
          case 'enriched': return !!lead.person_id_to_contact_id;
          case 'not-enriched': return !lead.person_id_to_contact_id;
          case 'lost': return lead.status === 'lost';
          case 'marked-ok': return lead.markedOK === true;
          case 'marked-not-ok': return lead.notOK === true;
          case 'unmarked': return lead.notOK !== true && lead.markedOK !== true;
          default: return true;
        }
      });
      if (!matchesAll) return false;
    }

    return true;
  });
};

// Get leads with filters
export const getLeads = async (filters: LeadsQueryFilters): Promise<Lead[]> => {
  try {
    const { compositeFilter, constraints } = buildLeadsQueryConstraints(filters);
    const collectionRef = collection(db, 'leads') as CollectionReference<DocumentData>;
    const q = compositeFilter
      ? constraints.length > 0
        ? query(collectionRef, and(compositeFilter, ...(constraints as QueryFilterConstraint[])))
        : query(collectionRef, compositeFilter)
      : query(collectionRef, ...constraints);
    const querySnapshot = await getDocs(q);
    const leads = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as Lead[];
    return applyClientSideFilters(leads, filters);
  } catch (error) {
    console.error('Error getting leads:', error);
    throw error;
  }
};

/**
 * Fetches the actual count of lead documents for each business type slug
 * using Firestore count() aggregation queries (1 read per type, regardless
 * of how many lead docs match).
 */
export const getLeadCountsByBusinessTypes = async (
  slugs: string[]
): Promise<Record<string, number>> => {
  if (slugs.length === 0) return {};
  const leadsRef = collection(db, 'leads') as CollectionReference<DocumentData>;
  const entries = await Promise.all(
    slugs.map(async (slug) => {
      const q = query(leadsRef, where('scope.business_type', '==', slug));
      const snapshot = await getCountFromServer(q);
      return [slug, snapshot.data().count] as const;
    })
  );
  return Object.fromEntries(entries);
};

// Subscribe to leads with filters
export const subscribeToLeads = (
  filters: LeadsQueryFilters,
  callback: (leads: Lead[]) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const { compositeFilter, constraints } = buildLeadsQueryConstraints(filters);
    const collectionRef = collection(db, 'leads') as CollectionReference<DocumentData>;
    const q = compositeFilter
      ? constraints.length > 0
        ? query(collectionRef, and(compositeFilter, ...(constraints as QueryFilterConstraint[])))
        : query(collectionRef, compositeFilter)
      : query(collectionRef, ...constraints);

    return onSnapshot(
      q,
      (querySnapshot) => {
        let leads = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Lead[];

        // Apply client-side filters
        leads = applyClientSideFilters(leads, filters);

        callback(leads);
      },
      (error) => {
        console.error('Error subscribing to leads:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up leads subscription:', error);
    onError?.(error as Error);
    return () => {};
  }
};

// Update lead WhatsApp number
export const updateLeadWhatsappNumber = async (
  leadId: string,
  whatsappNumber: string
): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      'contacts.whatsappNumber': whatsappNumber,
    });
  } catch (error) {
    console.error('Error updating lead WhatsApp number:', error);
    throw error;
  }
};

// Update lead notOK status
export const updateLeadNotOkStatus = async (leadId: string, notOK: boolean): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      notOK,
    });
  } catch (error) {
    console.error('Error updating lead notOK status:', error);
    throw error;
  }
};

// Update lead rating (3-state: ok, not-ok, neutral)
export type LeadRating = 'ok' | 'not-ok' | 'neutral';

export const updateLeadRating = async (leadId: string, rating: LeadRating): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      notOK: rating === 'not-ok',
      markedOK: rating === 'ok',
    });
  } catch (error) {
    console.error('Error updating lead rating:', error);
    throw error;
  }
};

// Delete a lead permanently
export const deleteLead = async (leadId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('Error deleting lead:', error);
    throw error;
  }
};

// Check if a lead with the given domain already exists
export const checkLeadExists = async (domain: string): Promise<boolean> => {
  const docRef = doc(db, 'leads', domain);
  const docSnap = await getDoc(docRef);
  return docSnap.exists();
};

// Extract naked domain from a URL (strips protocol, www, path, query, hash)
export const extractNakedDomain = (url: string): string => {
  try {
    // Ensure the URL has a protocol for the URL constructor
    const normalizedUrl = url.match(/^https?:\/\//) ? url : `https://${url}`;
    const parsed = new URL(normalizedUrl);
    return parsed.hostname.replace(/^www\./, '');
  } catch {
    // Fallback: strip common prefixes manually
    return url
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .split('/')[0]!
      .split('?')[0]!
      .split('#')[0]!;
  }
};

// Create a manual lead document
export const createManualLead = async (data: {
  domain: string;
  url: string;
  name: string;
  businessTypeSlug: string;
  cityId: string;
  cityName: string;
  metadata: BusinessTypeMetadata;
}): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', data.domain);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      throw new Error('A lead with this domain already exists');
    }

    await setDoc(docRef, {
      created: serverTimestamp(),
      name: data.name,
      url: data.url,
      metadata: {
        website_objectives: data.metadata.website_objectives,
        industry_painpoints: data.metadata.industry_painpoints,
      },
      scope: {
        business_type: data.businessTypeSlug,
        cities: {
          id: data.cityId,
          name: data.cityName,
        },
      },
    });

    // Append history entry — the aggregation trigger recalculates output from history
    const businessTypeRef = doc(db, 'scopes', 'types', 'business_types', data.businessTypeSlug);
    await updateDoc(businessTypeRef, {
      history: arrayUnion({
        id: data.cityId,
        name: data.name,
        output: 1,
        request: 1,
        ts: FirestoreTimestamp.now(),
        type: 'manual',
      }),
    });
  } catch (error) {
    console.error('Error creating manual lead:', error);
    throw error;
  }
};

// Enrich a contact person (triggers Apollo enrichment)
export const enrichContactPerson = async (leadId: string, personId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    const docSnap = await getDoc(docRef);

    if (!docSnap.exists()) {
      throw new Error('Lead not found');
    }

    const leadData = docSnap.data() as Lead;
    const people = leadData.contacts?.apollo?.people || [];

    // Find the person and mark as enrich
    const updatedPeople = people.map((person) => {
      if (person.id === personId) {
        return { ...person, enrich: true };
      }
      return person;
    });

    // Update the lead with the enrich flag
    await updateDoc(docRef, {
      'contacts.apollo.people': updatedPeople,
    });
  } catch (error) {
    console.error('Error enriching contact person:', error);
    throw error;
  }
};

// Connected Account interface
export interface ConnectedAccount {
  onboard_uid?: string;
}

// Get connected account for a user
export const getConnectedAccount = async (userId: string): Promise<ConnectedAccount | null> => {
  try {
    const docRef = doc(db, 'connected-accounts', userId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return docSnap.data() as ConnectedAccount;
    }
    return null;
  } catch (error) {
    console.error('Error getting connected account:', error);
    throw error;
  }
};

// Subscribe to connected account changes
export const subscribeToConnectedAccount = (
  userId: string,
  callback: (account: ConnectedAccount | null) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const docRef = doc(db, 'connected-accounts', userId);

    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          callback(docSnap.data() as ConnectedAccount);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error subscribing to connected account:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up connected account subscription:', error);
    onError?.(error as Error);
    return () => {};
  }
};

// Trigger onboarding for a lead by writing onboard_uid
export const triggerLeadOnboarding = async (leadId: string, onboardUid: string): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      onboard_uid: onboardUid,
    });
  } catch (error) {
    console.error('Error triggering lead onboarding:', error);
    throw error;
  }
};

/**
 * Trigger GIF regeneration by writing demo_gif.regenerate = true on the lead document.
 * A backend Cloud Function watches this field and kicks off the webcapture pipeline.
 */
export const triggerGifRegeneration = async (leadId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      'demo_gif.regenerate': true,
    });
  } catch (error) {
    console.error('Error triggering GIF regeneration:', error);
    throw error;
  }
};

// Add a manually created contact to people_enriched array
export const addManualContact = async (
  leadId: string,
  contact: { firstName: string; lastName: string; title: string; email: string },
  organizationName?: string
): Promise<void> => {
  try {
    const id = crypto.randomUUID();

    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      'contacts.apollo.people_enriched': arrayUnion({
        id,
        first_name: contact.firstName,
        last_name: contact.lastName,
        name: `${contact.firstName} ${contact.lastName}`,
        title: contact.title,
        email: contact.email,
        organization_name: organizationName || '',
        organization: { name: organizationName || '' },
      }),
    });
  } catch (error) {
    console.error('Error adding manual contact:', error);
    throw error;
  }
};

// Delete a manually added contact from people_enriched array
export const deleteManualContact = async (leadId: string, personId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    const snap = await getDoc(docRef);
    const data = snap.data() as Lead | undefined;
    const enriched = data?.contacts?.apollo?.people_enriched ?? [];
    const filtered = enriched.filter((p) => p.id !== personId);
    await updateDoc(docRef, { 'contacts.apollo.people_enriched': filtered });
  } catch (error) {
    console.error('Error deleting manual contact:', error);
    throw error;
  }
};

// Add person to Apollo contacts by writing person_id_to_contact_id map entry with null
// This triggers the ApolloAddContactTrigger to create the contact in Apollo
export const addPersonToContacts = async (leadId: string, personId: string): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      [`person_id_to_contact_id.${personId}`]: null,
    });
  } catch (error) {
    console.error('Error adding person to contacts:', error);
    throw error;
  }
};

// Apollo Sequences functions
export const getSequencesList = async (): Promise<ApolloSequence[]> => {
  try {
    const docRef = doc(db, 'scopes', 'sequences');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.sequences || [];
    }
    return [];
  } catch (error) {
    console.error('Error getting sequences list:', error);
    throw error;
  }
};

// Add a contact to an Apollo sequence (writes to apollo_contacts subcollection)
export const addContactToSequence = async (
  leadId: string,
  contactId: string,
  sequenceId: string
): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId, 'apollo_contacts', contactId);
    await setDoc(
      docRef,
      {
        sequence_action: {
          sequence_id: sequenceId,
          status: 'pending' as SequenceActionStatus,
        },
      },
      { merge: true }
    );
  } catch (error) {
    console.error('Error adding contact to sequence:', error);
    throw error;
  }
};

// Subscribe to an apollo_contacts document for real-time status updates
export const subscribeToApolloContact = (
  leadId: string,
  contactId: string,
  callback: (data: ApolloContactDoc | null) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const docRef = doc(db, 'leads', leadId, 'apollo_contacts', contactId);
    return onSnapshot(
      docRef,
      (docSnap) => {
        if (docSnap.exists()) {
          callback(docSnap.data() as ApolloContactDoc);
        } else {
          callback(null);
        }
      },
      (error) => {
        console.error('Error subscribing to apollo contact:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up apollo contact subscription:', error);
    onError?.(error as Error);
    return () => {};
  }
};

// Subscribe to all apollo_contacts for a lead (collection-level)
export const subscribeToApolloContacts = (
  leadId: string,
  callback: (contacts: Map<string, ApolloContactDoc>) => void,
  onError?: (error: Error) => void
): Unsubscribe => {
  try {
    const collectionRef = collection(db, 'leads', leadId, 'apollo_contacts');
    return onSnapshot(
      collectionRef,
      (querySnapshot) => {
        const contactsMap = new Map<string, ApolloContactDoc>();
        querySnapshot.docs.forEach((docSnap) => {
          contactsMap.set(docSnap.id, docSnap.data() as ApolloContactDoc);
        });
        callback(contactsMap);
      },
      (error) => {
        console.error('Error subscribing to apollo contacts:', error);
        onError?.(error);
      }
    );
  } catch (error) {
    console.error('Error setting up apollo contacts subscription:', error);
    onError?.(error as Error);
    return () => {};
  }
};

// Get all apollo_contacts documents for a lead (reads subcollection)
export const getApolloContacts = async (
  leadId: string
): Promise<Map<string, ApolloContactDoc>> => {
  try {
    const colRef = collection(db, 'leads', leadId, 'apollo_contacts');
    const snapshot = await getDocs(colRef);
    const result = new Map<string, ApolloContactDoc>();
    snapshot.forEach((docSnap) => {
      result.set(docSnap.id, docSnap.data() as ApolloContactDoc);
    });
    return result;
  } catch (error) {
    console.error('Error getting apollo contacts:', error);
    return new Map();
  }
};

// Fetch a single apollo_events document for a specific contact
export const getApolloEvents = async (
  leadId: string,
  contactId: string
): Promise<ApolloEventDoc | null> => {
  try {
    const docRef = doc(db, 'leads', leadId, 'apollo_events', contactId);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as ApolloEventDoc;
    }
    return null;
  } catch (error) {
    console.error('Error getting apollo events:', error);
    return null;
  }
};

// Fetch apollo_events for multiple contacts at once
export const getApolloEventsForContacts = async (
  leadId: string,
  contactIds: string[]
): Promise<Map<string, ApolloEventDoc>> => {
  const result = new Map<string, ApolloEventDoc>();
  try {
    const promises = contactIds.map(async (contactId) => {
      const eventDoc = await getApolloEvents(leadId, contactId);
      if (eventDoc) {
        result.set(contactId, eventDoc);
      }
    });
    await Promise.all(promises);
  } catch (error) {
    console.error('Error getting apollo events for contacts:', error);
  }
  return result;
};

// Update the business type of a lead
export const updateLeadBusinessType = async (
  leadId: string,
  businessTypeSlug: string
): Promise<void> => {
  try {
    const docRef = doc(db, 'leads', leadId);
    await updateDoc(docRef, {
      'scope.business_type': businessTypeSlug,
    });
  } catch (error) {
    console.error('Error updating lead business type:', error);
    throw error;
  }
};

// Update default sequence ID on a business type (null to clear)
export const updateBusinessTypeDefaultSequence = async (
  slug: string,
  sequenceId: string | null
): Promise<void> => {
  try {
    const docRef = doc(db, 'scopes', 'types', 'business_types', slug);
    await updateDoc(docRef, {
      default_sequence_id: sequenceId,
    });
  } catch (error) {
    console.error('Error updating business type default sequence:', error);
    throw error;
  }
};
